/**
 * 9:16 share-card. Canvas-rendered, downloadable as PNG. Web Share API integrated.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatINR, MERCHANT_DISPLAY } from '../types.ts';
import type { Summary } from '../types.ts';

export class HisaabShareCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #current: Summary | null = null;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      this.#current = s.summary;
      this.#render();
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: grid;
  gap: 10px;
}
button {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 16px;
  background: var(--saffron);
  color: var(--paper);
  border-radius: 8px;
  font-weight: 700;
}
button:active { filter: brightness(0.92); }
button.ghost {
  background: transparent;
  color: var(--ink-dim);
  border: 1px solid var(--rule);
}
</style>
<button id="share">Share kahaani card</button>
<button class="ghost" id="download">Download 9:16 PNG</button>
`;
    this.#shadow.getElementById('share')?.addEventListener('click', () => this.#share());
    this.#shadow.getElementById('download')?.addEventListener('click', () => this.#download());
  }

  async #renderCanvas(): Promise<Blob | null> {
    if (!this.#current) return null;
    const W = 1080;
    const H = 1920;
    const canvas = new OffscreenCanvas(W, H);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const s = this.#current;
    const tier = s.insights.tier;
    const meta = TIER_DISPLAY[tier];

    ctx.fillStyle = '#0e0c2a';
    ctx.fillRect(0, 0, W, H);

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, 'rgba(245, 158, 11, 0.18)');
    grad.addColorStop(1, 'rgba(14, 12, 42, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#f6e7c7';
    ctx.font = '700 60px "Cormorant Garamond", serif';
    ctx.textAlign = 'left';
    ctx.fillText('हिसाब', 80, 200);

    ctx.fillStyle = 'rgba(246, 231, 199, 0.45)';
    ctx.font = '500 22px "Space Mono", monospace';
    ctx.fillText('HISAAB · MY 90-DAY KAHAANI', 80, 240);

    ctx.fillStyle = '#f59e0b';
    ctx.font = '500 32px "Space Mono", monospace';
    ctx.fillText('TIER', 80, 420);
    ctx.fillStyle = '#f6e7c7';
    ctx.font = 'italic 700 96px "Cormorant Garamond", serif';
    ctx.fillText(meta.name, 80, 520);
    ctx.fillStyle = 'rgba(246, 231, 199, 0.7)';
    ctx.font = 'italic 500 36px "Cormorant Garamond", serif';
    ctx.fillText(`"${meta.tagline}"`, 80, 580);

    ctx.fillStyle = 'rgba(246, 231, 199, 0.6)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('TOTAL · 90 days', 80, 760);
    ctx.fillStyle = '#f6e7c7';
    ctx.font = '700 140px "Cormorant Garamond", serif';
    ctx.fillText(formatINR(s.totals.inr), 80, 900);

    ctx.fillStyle = 'rgba(246, 231, 199, 0.6)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('MONTHLY AVG', 80, 970);
    ctx.fillStyle = '#f6e7c7';
    ctx.font = '700 64px "Cormorant Garamond", serif';
    ctx.fillText(formatINR(s.totals.monthly_avg), 80, 1040);

    const topMerchants = Object.entries(s.totals.by_merchant).slice(0, 4);
    ctx.fillStyle = '#f59e0b';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('TOP DUKAANDAR', 80, 1200);
    let y = 1260;
    for (const [m, v] of topMerchants) {
      const display = MERCHANT_DISPLAY[m] ?? m;
      ctx.fillStyle = '#f6e7c7';
      ctx.font = 'italic 500 44px "Cormorant Garamond", serif';
      ctx.fillText(display, 80, y);
      ctx.fillStyle = 'rgba(246, 231, 199, 0.55)';
      ctx.font = '700 36px "Cormorant Garamond", serif';
      ctx.textAlign = 'right';
      ctx.fillText(formatINR(v), W - 80, y);
      ctx.textAlign = 'left';
      y += 64;
    }

    ctx.fillStyle = 'rgba(246, 231, 199, 0.4)';
    ctx.font = '500 20px "Space Mono", monospace';
    ctx.fillText('hisaab.taxwalaai.com', 80, H - 100);
    ctx.fillStyle = '#f59e0b';
    ctx.fillText('▣', W - 110, H - 100);

    return await canvas.convertToBlob({ type: 'image/png' });
  }

  async #share(): Promise<void> {
    const blob = await this.#renderCanvas();
    if (!blob) return;
    const file = new File([blob], 'hisaab-kahaani.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'My Hisaab', text: 'My 90-day spend kahaani' });
      } catch {
        this.#download(blob);
      }
    } else {
      this.#download(blob);
    }
  }

  async #download(b?: Blob): Promise<void> {
    const blob = b ?? await this.#renderCanvas();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hisaab-kahaani.png';
    a.click();
    URL.revokeObjectURL(url);
  }
}

customElements.define('hisaab-share-card', HisaabShareCard);
